/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { Component, createContext, type ReactNode, useContext } from 'react';
import noop from 'lodash/noop';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { LazyComponent as AsyncProjectBuilderDrawerComponent } from '@jira/servicedesk__project-builder/src/AsyncProjectBuilderDrawer.tsx';
import { getIsJsmBlankProjectTemplate } from '@jira/platform__common-constants/src/project-template-type-keys.tsx';
import { useRouterActions, type RouterActionsType } from '@atlassian/react-resource-router';
import type {
	ConsumerData,
	OpenDrawerMethod,
	ProjectCreateSuccessCallbackPayload,
} from './types.tsx';
import { clearResourcesAssociatedWithProjectKey } from './utils.tsx';

// oxlint-disable-next-line typescript/consistent-type-assertions
const Context = createContext<ConsumerData>({
	data: {
		shouldRenderDrawer: false,
		isOpen: false,
		isNextGen: false,
		showExperienceSelection: true,
		recommendedTemplate: undefined,
		defaultSelectedTemplateKey: '',
		source: '',
		isTemplatePickerOpenInitially: false,
		isImportProjectOpenInitially: false,
		selectedProjectType: null,
		recommendationSessionId: undefined,
	},
	methods: {
		open: noop,
		close: noop,
		closeAndRun: noop,
		onSuccess: noop,
		onCreateStarted: noop,
		setOnCloseCallback: () => noop,
		clearOnCloseCallback: noop,
		setOnCreatedCallback: noop,
		clearOnCreatedCallback: noop,
	},
} as ConsumerData);

export type Props = {
	children: ReactNode;
	setLocation: RouterActionsType['push'];
};
type State = {
	isOpen: boolean;
	isOnboarding: boolean;
	shouldRenderDrawer: boolean;
	isNextGen: boolean;
	showExperienceSelection: boolean;
	recommendedTemplate:
		| undefined
		| {
				templateKey: string;
				isSimplified: boolean;
		  };
	defaultSelectedTemplateKey: string;
	source?: string;
	isTemplatePickerOpenInitially: boolean;
	isImportProjectOpenInitially: boolean;
	selectedProjectType: ProjectType | null;
	recommendationSessionId?: string;
	onCloseCallback: () => void;
	onCreatedCallback: (project: ProjectCreateSuccessCallbackPayload) => void;
};
// eslint-disable-next-line jira/react/no-class-components
export class ProviderInternal extends Component<Props, State> {
	state: {
		isOpen: boolean;
		isOnboarding: boolean;
		shouldRenderDrawer: boolean;
		isNextGen: boolean;
		showExperienceSelection: boolean;
		recommendedTemplate: undefined;
		defaultSelectedTemplateKey: string;
		source: string;
		isTemplatePickerOpenInitially: boolean;
		isImportProjectOpenInitially: boolean;
		selectedProjectType: null;
		recommendationSessionId: undefined;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		onCloseCallback: (...args: any[]) => void;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		onCreatedCallback: (...args: any[]) => void;
	} = {
		isOpen: false,
		isOnboarding: false,
		shouldRenderDrawer: false,
		isNextGen: false,
		showExperienceSelection: true,
		recommendedTemplate: undefined,
		defaultSelectedTemplateKey: '',
		source: '',
		isTemplatePickerOpenInitially: false,
		isImportProjectOpenInitially: false,
		selectedProjectType: null,
		recommendationSessionId: undefined,
		onCloseCallback: noop,
		onCreatedCallback: noop,
	};

	onSuccess = (project?: ProjectCreateSuccessCallbackPayload): void => {
		if (project) {
			if (project.requiresReload && typeof window !== 'undefined') {
				window.location.href = project.returnUrl;
			} else {
				this.props.setLocation(project.returnUrl);
			}

			this.close(true);

			this.state.onCreatedCallback(project);
		} else {
			this.close(false);
		}
	};

	// Callback that is called once when the drawer is closed.
	//
	// Returns the PREVIOUSLY registered callback so the caller can restore it
	// on unmount and avoid clobbering a host's registration with `noop`.
	// eslint-disable-next-line jira/react-arrow-function-property-naming
	setOnCloseCallback = (callback: () => void): (() => void) => {
		const previousCallback = this.state.onCloseCallback;
		this.setState({
			onCloseCallback: callback,
		});
		return previousCallback;
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	clearOnCloseCallback = (): void => {
		this.setState({ onCloseCallback: noop });
	};

	// Callback that is called once when a project is successfully created.
	// eslint-disable-next-line jira/react-arrow-function-property-naming
	setOnCreatedCallback = (
		callback: (project: ProjectCreateSuccessCallbackPayload) => void,
	): void => {
		this.setState({
			onCreatedCallback: callback,
		});
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	clearOnCreatedCallback = (): void => {
		this.setState({ onCreatedCallback: noop });
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	open: OpenDrawerMethod = (attributes = {}) => {
		// @ts-expect-error - TS2345 - Argument of type '{ isNextGen?: boolean | undefined; showExperienceSelection?: boolean | undefined; recommendedTemplate?: { templateKey: string; isSimplified: boolean; } | undefined; defaultSelectedTemplateKey?: string | undefined; ... 4 more ...; shouldRenderDrawer: true; }' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, "source" | "isOpen" | "isNextGen" | ... 5 more ... | "isTemplatePickerOpenInitially"> | null) | Pick<...> | null'.
		this.setState({
			isOpen: true,
			shouldRenderDrawer: true,
			...attributes,
		});
	};

	// Resets the drawer back to its closed/default state. Shared by `close` and
	// `closeAndRun` so the reset behaviour cannot drift between them.
	private resetDrawerState(): void {
		this.setState({
			isOpen: false,
			isOnboarding: false,
			isNextGen: false,
			showExperienceSelection: true,
			recommendedTemplate: undefined,
			defaultSelectedTemplateKey: '',
			source: '',
			isTemplatePickerOpenInitially: false,
			isImportProjectOpenInitially: false,
			selectedProjectType: null,
			recommendationSessionId: undefined,
		});
	}

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	close = (projectCreated?: boolean): void => {
		const callOnCloseCallBack = this.state.source === 'navigationNext' || !projectCreated;
		this.resetDrawerState();

		if (callOnCloseCallBack) {
			// Call the added callback
			this.state.onCloseCallback();
		}
	};

	// Closes the drawer and runs `callback` exactly once for THIS close. The
	// callback is supplied by value (not read from state), so it always runs —
	// no reliance on `setState` having committed. The persisted
	// `onCloseCallback` is intentionally NOT invoked here.
	// eslint-disable-next-line jira/react-arrow-function-property-naming
	closeAndRun = (callback: () => void): void => {
		this.resetDrawerState();
		callback();
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming, jira/react-no-unneeded-arrow-function-properties
	closeOld = (): void => {
		this.setState({
			isOpen: false,
			isOnboarding: false,
			isNextGen: false,
			showExperienceSelection: true,
			recommendedTemplate: undefined,
			defaultSelectedTemplateKey: '',
			source: '',
			isTemplatePickerOpenInitially: false,
			selectedProjectType: null,
			recommendationSessionId: undefined,
		});
		this.state.onCloseCallback();
	};

	onCreateStarted = (projectKey: string, templateKey: string): void => {
		if (getIsJsmBlankProjectTemplate(templateKey)) {
			AsyncProjectBuilderDrawerComponent.preload();
		}
		clearResourcesAssociatedWithProjectKey(projectKey);
	};

	render(): React.JSX.Element {
		const { children } = this.props;
		const {
			isOpen,
			isNextGen,
			source,
			showExperienceSelection,
			shouldRenderDrawer,
			recommendedTemplate,
			defaultSelectedTemplateKey,
			isTemplatePickerOpenInitially,
			isImportProjectOpenInitially,
			selectedProjectType,
			recommendationSessionId,
			isOnboarding,
		} = this.state;

		return (
			<Context.Provider
				value={{
					data: {
						isOpen,
						isNextGen,
						isOnboarding,
						source,
						showExperienceSelection,
						shouldRenderDrawer,
						recommendedTemplate,
						defaultSelectedTemplateKey,
						isTemplatePickerOpenInitially,
						isImportProjectOpenInitially,
						selectedProjectType,
						recommendationSessionId,
					},
					methods: {
						open: this.open,
						close: this.close,
						closeAndRun: this.closeAndRun,
						onSuccess: this.onSuccess,
						onCreateStarted: this.onCreateStarted,
						setOnCloseCallback: this.setOnCloseCallback,
						clearOnCloseCallback: this.clearOnCloseCallback,
						setOnCreatedCallback: this.setOnCreatedCallback,
						clearOnCreatedCallback: this.clearOnCreatedCallback,
					},
				}}
			>
				{children}
			</Context.Provider>
		);
	}
}

export const ProjectCreateDrawerProvider = (props: { children: ReactNode }): React.JSX.Element => {
	const { push } = useRouterActions();
	return <ProviderInternal {...props} setLocation={push} />;
};

export const ProjectCreateDrawerConsumer: React.Consumer<ConsumerData> = Context.Consumer;

export const useProjectCreateDrawer = (): ConsumerData => {
	const projectCreateDrawer = useContext(Context);
	if (!projectCreateDrawer) {
		throw new Error('useProjectCreateDrawer must be used within a ProjectCreateDrawerProvider');
	}
	return projectCreateDrawer;
};
